import { useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import * as apiProduct from 'api/apiProducts';
import EToolTip from 'components/others/EToolTip';
import FormInput from 'components/forms/FormInput';
import SolarIcon from 'components/icons/SolarIcon';

interface VendorItem {
    ID: number;
    vendorName: string;
    vendorEmail: string;
}

interface VendorsListProps {
    vendors: VendorItem[];
    usage: Record<number, number>;
    onChanged: () => Promise<void>;
}

const VendorsList = ({ vendors, usage, onChanged }: VendorsListProps) => {
    const { t } = useTranslation(['Products', 'common']);
    const { showDlg } = useModal();
    const { showToast } = useToast();
    const [editEmailId, setEditEmailId] = useState<number | null>(null);

    const handleSaveEmail = async (vendor: VendorItem, value: string | number) => {
        const email = String(value || '').trim();
        if (email === (vendor.vendorEmail || '')) {
            setEditEmailId(null);
            return;
        }

        const res = await apiProduct.updateVendorEmail(vendor.ID, email || null);
        if (res?.stat) {
            await onChanged();
            setEditEmailId(null);
            showToast(t('vendorsEditor.messEmailUpdated'), 'success');
            return;
        }

        if (res?.errCode === 'INVALID_EMAIL') {
            showToast(t('vendorsEditor.messInvalidEmail'), 'danger');
            return false;
        }

        showToast(t('common:messError'), 'danger');
        return false;
    };

    const handleDelete = (vendor: VendorItem) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            const res = await apiProduct.deleteVendor(vendor.ID);
            if (res?.stat) {
                await onChanged();
                showToast(t('vendorsEditor.messDeleted'), 'success');
                return;
            }

            if (res?.errCode === 'IN_USE') {
                showToast(t('vendorsEditor.messInUse'), 'danger');
                return;
            }

            showToast(t('common:messError'), 'danger');
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: t('vendorsEditor.confirmDelete', { name: vendor.vendorName }),
        });
    };

    if (!vendors.length) {
        return <p className="text-muted px-3 mb-0">{t('vendorsEditor.emptyList')}</p>;
    }

    return (
        <div className="crm-table-wrap px-3">
            <Table size="sm" className="VendorsDirTable mb-0">
                <thead>
                    <tr className="bg-light">
                        <td className="head-id">{t('vendorsEditor.colId')}</td>
                        <td className="head-name">{t('vendorsEditor.colName')}</td>
                        <td className="head-products text-end">{t('vendorsEditor.colProducts')}</td>
                        <td className="head-action"></td>
                    </tr>
                </thead>
                <tbody>
                    {vendors.map((vendor) => {
                        const productCount = usage[vendor.ID] || 0;
                        const canDelete = productCount === 0;
                        const isEditingEmail = editEmailId === vendor.ID;

                        return (
                            <tr key={vendor.ID}>
                                <td className="id">
                                    <code>{vendor.ID}</code>
                                </td>
                                <td className="name">
                                    <div className="fw-medium">{vendor.vendorName}</div>
                                    {isEditingEmail ? (
                                        <FormInput
                                            compact
                                            type="email"
                                            showForm
                                            setShowForm={(show) => {
                                                if (!show) setEditEmailId(null);
                                            }}
                                            defaultValue={vendor.vendorEmail}
                                            invalidEmailMessage={t('vendorsEditor.messInvalidEmail')}
                                            callBack={(value) => handleSaveEmail(vendor, value)}
                                        />
                                    ) : (
                                        <p
                                            className="f-12 mb-0 text-primary product-vendor-editable"
                                            role="button"
                                            tabIndex={0}
                                            title={t('vendorsEditor.emailEditHint')}
                                            onClick={() => setEditEmailId(vendor.ID)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setEditEmailId(vendor.ID);
                                                }
                                            }}
                                        >
                                            {vendor.vendorEmail || t('vendorsEditor.emailPlaceholder')}
                                        </p>
                                    )}
                                </td>
                                <td className="products text-end text-muted">{productCount}</td>
                                <td className="action text-end">
                                    <EToolTip
                                        title={canDelete ? t('vendorsEditor.deleteHint') : t('vendorsEditor.deleteDisabled')}
                                    >
                                        <span className="d-inline-block">
                                            <Button
                                                type="button"
                                                variant="link"
                                                className={`p-0 ${canDelete ? 'text-danger' : 'text-muted'}`}
                                                disabled={!canDelete}
                                                onClick={() => handleDelete(vendor)}
                                            >
                                                <SolarIcon name="trash" />
                                            </Button>
                                        </span>
                                    </EToolTip>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
};

export default VendorsList;
